import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from './store.js'

/**
 * CDN 域名替换 composable
 * 根据 store 中的 siteInfo.configData.cdnDomainMap 替换 URL 中的域名
 */
export function useCdnDomain() {
  const store = useMainStore()
  
  // 使用 storeToRefs 来保持响应式，直接访问 store 的 state
  // 这样在 SSR 和客户端都能正确响应 store 的变化
  const { siteInfo } = storeToRefs(store)

  /**
   * 获取 cdnDomainMap（响应式）
   * @returns {Object} cdnDomainMap 对象，key 为原始域名，value 为替换后的域名
   */
  const getCdnDomainMap = () => {
    if (!siteInfo.value || !siteInfo.value.configData) {
      return {}
    }

    let configData = siteInfo.value.configData

    // 如果 configData 是字符串，需要解析
    if (typeof configData === 'string') {
      try {
        configData = JSON.parse(configData)
      } catch (e) {
        console.error('Failed to parse configData:', e)
        return {}
      }
    }

    // 获取 cdnDomainMap
    const cdnDomainMap = configData?.cdnDomainMap || {}
    return cdnDomainMap
  }

  /**
   * 响应式的 cdnDomainMap（用于监听变化）
   */
  const cdnDomainMap = computed(() => {
    return getCdnDomainMap()
  })

  /**
   * 替换 URL 中的域名（响应式版本）
   * 注意：这个函数应该在 computed 中使用，以确保响应式追踪
   * @param {string} url - 原始 URL
   * @returns {string} 替换后的 URL
   */
  const replaceCdnDomain = (url) => {
    if (!url || typeof url !== 'string') {
      return url
    }

    // 直接访问响应式的 siteInfo 和 cdnDomainMap，确保响应式追踪
    // 这样当在 computed 中调用时，Vue 能够追踪到依赖
    if (!siteInfo.value || !siteInfo.value.configData) {
      return url
    }

    let configData = siteInfo.value.configData

    // 如果 configData 是字符串，需要解析
    if (typeof configData === 'string') {
      try {
        configData = JSON.parse(configData)
      } catch (e) {
        console.error('Failed to parse configData:', e)
        return url
      }
    }

    const domainMap = configData?.cdnDomainMap || {}
    
    if (!domainMap || Object.keys(domainMap).length === 0) {
      // SSR 时如果数据还没加载，返回原始 URL
      return url
    }

    try {
      // 解析 URL
      const urlObj = new URL(url)
      const originalHost = urlObj.host

      // 遍历 cdnDomainMap，查找匹配的域名
      for (const [key, value] of Object.entries(domainMap)) {
        // 检查原始域名是否包含 key（支持部分匹配）
        if (originalHost.includes(key)) {
          // 如果包含匹配的域名，直接替换整个 host 为 value
          urlObj.host = value
          return urlObj.toString()
        }
      }

      // 如果没有匹配到，返回原始 URL
      return url
    } catch (e) {
      // 如果 URL 解析失败（可能是相对路径），尝试字符串替换
      // 遍历 cdnDomainMap，进行字符串替换
      let replacedUrl = url
      for (const [key, value] of Object.entries(domainMap)) {
        if (replacedUrl.includes(key)) {
          // 使用正则表达式替换，确保只替换域名部分
          // 匹配 http:// 或 https:// 后的域名部分
          const regex = new RegExp(`(https?://)${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi')
          replacedUrl = replacedUrl.replace(regex, `$1${value}`)
        }
      }
      return replacedUrl
    }
  }

  return {
    cdnDomainMap,
    getCdnDomainMap,
    replaceCdnDomain,
  }
}
